// src/pages/seeker/SeekerSkills.jsx

import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';
import { useConfirm, EditBtn, AddBtn, DelBtn } from '../../components/shared/UI';

const C = {
  blue:       '#3D80CC',
  white:      '#ffffff',
  offwhite:   '#f4f7fb',
  border:     '#d0daea',
  muted:      '#666',
  pillBg:     '#e8edf4',
  pillText:   '#2c3e6b',
  pillBorder: '#c5d0e0',
};

const SYSTEM_SECTIONS = [
  { systemKey: 'SOFT_SKILLS', title: 'Soft Skills' },
  { systemKey: 'SOFTWARE',    title: 'Software & Applications' },
  { systemKey: 'HARDWARE',    title: 'Hardware & Equipment' },
];

// ── Skill pill ─────────────────────────────────────────────
const SkillPill = ({ label, onRemove }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 0,
    padding: '3px 4px 3px 10px', fontSize: '12px', fontWeight: 600,
    background: C.pillBg, color: C.pillText,
    borderRadius: '20px', border: `1px solid ${C.pillBorder}`,
  }}>
    {label}
    <DelBtn onClick={onRemove} size={10} title="Remove" />
  </span>
);

// ── Section card — renders as its own section-box ──────────
const SectionCard = ({ section, allSkills, allocatedSkillIds, isSystem, onRename, onDelete, onAddSkill, onRemoveSkill }) => {
  const [editing,  setEditing]  = useState(false);
  const [title,    setTitle]    = useState(section.title);
  const [saving,   setSaving]   = useState(false);
  const titleRef = useRef();

  const sectionSkillIds = new Set(section.skills.map(s => s.jobSeekerSkillId));
  // Skills available to add: not already in this section AND not allocated to any other section
  const available = allSkills.filter(jss => !sectionSkillIds.has(jss.id) && !allocatedSkillIds.has(jss.id));

  const commitRename = async () => {
    const t = title.trim();
    if (!t || t === section.title) { setTitle(section.title); setEditing(false); return; }
    setSaving(true);
    await onRename(section.id, t);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="section-box" style={{ marginBottom: 20 }}>
      {/* Header */}
      <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {editing ? (
          <input
            ref={titleRef}
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setTitle(section.title); setEditing(false); } }}
            onBlur={commitRename}
            disabled={saving}
            autoFocus
            style={{
              flex: 1, fontSize: 13, fontWeight: 700, background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4,
              color: 'white', padding: '2px 8px', outline: 'none',
            }}
          />
        ) : (
          <span style={{ flex: 1 }}>{section.title}</span>
        )}

        {!isSystem && !editing && (
          <EditBtn onClick={() => setEditing(true)} title="Rename section" size={12} />
        )}
        {!isSystem && (
          <DelBtn onClick={() => onDelete(section)} title="Delete section" size={12} />
        )}
      </div>

      {/* Body */}
      <div className="section-box-body">
        {section.skills.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {section.skills.map(s => (
              <SkillPill
                key={s.id}
                label={s.jobSeekerSkill?.skill?.displayName || s.displayName || ''}
                onRemove={() => onRemoveSkill(section.id, s.jobSeekerSkillId)}
              />
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', margin: '0 0 10px' }}>
            No skills added to this section yet.
          </p>
        )}

        {available.length > 0 ? (
          <select
            value=""
            onChange={e => { if (e.target.value) onAddSkill(section.id, e.target.value); }}
            style={{
              fontSize: 12, padding: '5px 8px', borderRadius: 5,
              border: `1px solid ${C.border}`, background: C.white,
              color: C.muted, cursor: 'pointer', maxWidth: 260,
            }}
          >
            <option value="">+ Add skill to this section…</option>
            {[...available].sort((a, b) => a.skill.displayName.localeCompare(b.skill.displayName)).map(jss => (
              <option key={jss.id} value={jss.id}>{jss.skill.displayName}</option>
            ))}
          </select>
        ) : (
          <p style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', margin: 0 }}>
            {allSkills.length === 0
              ? 'Add skills to your profile above first.'
              : 'All available skills are already assigned to a section.'}
          </p>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
export const SeekerSkills = () => {
  const { confirm, dialog } = useConfirm();
  const [skills,    setSkills]    = useState([]);
  const [sections,  setSections]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  // Add-skill input
  const [input,     setInput]     = useState('');
  const [sugg,      setSugg]      = useState([]);
  const [showSug,   setShowSug]   = useState(false);
  const skillInputRef = useRef();

  // New section form
  const [newTitle,  setNewTitle]  = useState('');
  const [creating,  setCreating]  = useState(false);

  // ── Load ──────────────────────────────────────────────────
  const load = async () => {
    try {
      const [profileRes, derivRes] = await Promise.all([
        seekerAPI.getProfile(),
        seekerAPI.getSkillDerivations(),
      ]);
      setSkills(profileRes.data.data.skills || []);
      const loadedSections = derivRes.data.data.customSections || [];
      setSections(loadedSections);

      // Ensure system sections exist — create any that are missing
      for (const sys of SYSTEM_SECTIONS) {
        if (!loadedSections.find(s => s.systemKey === sys.systemKey)) {
          try {
            const res = await seekerAPI.createCustomSection({ title: sys.title, systemKey: sys.systemKey });
            loadedSections.push(res.data.data);
          } catch { /* silently ignore — already exists or network error */ }
        }
      }
      setSections([...loadedSections]);
    } catch { toast.error('Failed to load skills'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // ── Autocomplete ──────────────────────────────────────────
  const fetchSugg = async (q) => {
    if (!q.trim()) { setSugg([]); return; }
    try {
      const res = await seekerAPI.searchSkills(q);
      const existing = new Set(skills.map(s => s.skill?.displayName?.toLowerCase()));
      setSugg((res.data.data || []).filter(s => !existing.has(s.displayName?.toLowerCase())));
    } catch { setSugg([]); }
  };

  // ── Add skill ─────────────────────────────────────────────
  const addSkill = async (displayName) => {
    const name = displayName.trim();
    if (!name) return;
    if (skills.some(s => s.skill?.displayName?.toLowerCase() === name.toLowerCase())) {
      toast.error(`"${name}" is already in your skills`); return;
    }
    setInput(''); setSugg([]); setShowSug(false);
    try {
      const res = await seekerAPI.addSkill({ displayName: name });
      setSkills(prev => [...prev, res.data.data]);
    } catch (err) {
      if (err?.response?.data?.message !== 'Skill already added')
        toast.error(err?.response?.data?.message || 'Failed to save skill');
    }
  };

  // ── Delete skill ──────────────────────────────────────────
  const deleteSkill = async (jss) => {
    if (!await confirm('Remove Skill', `Remove "${jss.skill.displayName}" from your profile?`)) return;
    setSkills(prev => prev.filter(s => s.id !== jss.id));
    setSections(prev => prev.map(sec => ({
      ...sec,
      skills: sec.skills.filter(s => s.jobSeekerSkillId !== jss.id),
    })));
    try { await seekerAPI.removeSkill(jss.skillId); }
    catch { toast.error('Failed to delete skill'); load(); }
  };

  // ── Custom section — create ────────────────────────────────
  const handleCreateSection = async () => {
    const t = newTitle.trim();
    if (!t) return;
    try {
      const res = await seekerAPI.createCustomSection({ title: t });
      setSections(prev => [...prev, { ...res.data.data, skills: [] }]);
      setNewTitle(''); setCreating(false);
    } catch { /* interceptor */ }
  };

  // ── Custom section — rename ────────────────────────────────
  const handleRename = async (sectionId, newName) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, title: newName } : s));
    try { await seekerAPI.updateCustomSection(sectionId, { title: newName }); }
    catch { toast.error('Failed to rename section'); load(); }
  };

  // ── Custom section — delete ────────────────────────────────
  const handleDeleteSection = async (section) => {
    if (!await confirm('Delete Section', `Delete the "${section.title}" section? Skills in your profile will not be removed.`)) return;
    setSections(prev => prev.filter(s => s.id !== section.id));
    try { await seekerAPI.deleteCustomSection(section.id); }
    catch { toast.error('Failed to delete section'); load(); }
  };

  // ── Section — add skill ────────────────────────────────────
  const handleAddSkillToSection = async (sectionId, jobSeekerSkillId) => {
    const jss = skills.find(s => s.id === jobSeekerSkillId);
    if (!jss) return;
    const tempLink = { id: `tmp-${Date.now()}`, jobSeekerSkillId, jobSeekerSkill: jss };
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, skills: [...s.skills, tempLink] } : s));
    try {
      const res = await seekerAPI.addSkillToCustomSection(sectionId, { jobSeekerSkillId });
      setSections(prev => prev.map(s => s.id === sectionId
        ? { ...s, skills: s.skills.map(sk => sk.id === tempLink.id ? { ...res.data.data, jobSeekerSkill: jss } : sk) }
        : s));
    } catch { toast.error('Failed to add skill'); load(); }
  };

  // ── Section — remove skill ─────────────────────────────────
  const handleRemoveSkillFromSection = async (sectionId, jobSeekerSkillId) => {
    setSections(prev => prev.map(s => s.id === sectionId
      ? { ...s, skills: s.skills.filter(sk => sk.jobSeekerSkillId !== jobSeekerSkillId) }
      : s));
    try { await seekerAPI.removeSkillFromCustomSection(sectionId, jobSeekerSkillId); }
    catch { toast.error('Failed to remove skill'); load(); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  const systemSections = sections.filter(s => s.systemKey);
  const customSections = sections.filter(s => !s.systemKey);

  // All skill IDs that are already assigned to any section
  const allocatedSkillIds = new Set(
    sections.flatMap(s => s.skills.map(sk => sk.jobSeekerSkillId))
  );

  const sectionProps = {
    allSkills: skills,
    allocatedSkillIds,
    onRename: handleRename,
    onDelete: handleDeleteSection,
    onAddSkill: handleAddSkillToSection,
    onRemoveSkill: handleRemoveSkillFromSection,
  };

  // Autocomplete dropdown rendered via portal to escape overflow:hidden
  const inputEl = skillInputRef.current;
  const inputRect = inputEl ? inputEl.getBoundingClientRect() : null;
  const skillDropdown = showSug && sugg.length > 0 && inputRect
    ? ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: inputRect.bottom + 2,
          left: inputRect.left,
          width: inputRect.width,
          zIndex: 9999,
          background: 'white',
          border: '1px solid var(--border)',
          borderRadius: '3px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '160px',
          overflowY: 'auto',
        }}>
          {sugg.map(s => (
            <button key={s.id} onMouseDown={() => addSkill(s.displayName)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = C.offwhite}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              {s.displayName}
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div className="fade-in">
      {dialog}
      <div className="page-header">
        <h1>Skills & Knowledge</h1>
        <p>Manage your skills and organise them into sections for your CVs.</p>
      </div>

      <div style={{ maxWidth: '760px' }}>

        {/* ── MY SKILLS ── */}
        <div className="section-box" style={{ marginBottom: 20, border: '2px solid #3D80CC' }}>
          <div className="section-box-header" style={{ background: '#3D80CC', fontSize: '14px', letterSpacing: '0.04em' }}>My Skills</div>
          <div className="section-box-body" style={{ background: '#f0f6ff' }}>
            {skills.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {[...skills].sort((a, b) => a.skill.displayName.localeCompare(b.skill.displayName)).map(jss => (
                  <SkillPill key={jss.id} label={jss.skill.displayName} onRemove={() => deleteSkill(jss)} />
                ))}
              </div>
            )}
            <div style={{ maxWidth: 360 }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  ref={skillInputRef}
                  value={input}
                  onChange={e => { setInput(e.target.value.slice(0, 64)); fetchSugg(e.target.value); setShowSug(true); }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); showSug && sugg.length ? addSkill(sugg[0].displayName) : (input.trim() && addSkill(input.trim())); }
                    if (e.key === 'Escape') setShowSug(false);
                  }}
                  onFocus={() => { if (input.trim()) setShowSug(true); }}
                  onBlur={() => setTimeout(() => setShowSug(false), 150)}
                  placeholder="Search or type a skill…"
                  className="form-input"
                  style={{ fontSize: '12px', padding: '6px 10px' }}
                />
                <button onClick={() => input.trim() && addSkill(input.trim())} className="btn btn-primary btn-sm" style={{ whiteSpace: 'nowrap', fontSize: '12px', padding: '4px 10px' }}>
                  + Add
                </button>
              </div>
            </div>
            {skillDropdown}
          </div>
        </div>

        {/* ── SYSTEM SECTIONS (Software & Hardware) ── */}
        {systemSections.map(section => (
          <SectionCard key={section.id} section={section} isSystem {...sectionProps} />
        ))}

        {/* ── CUSTOM SECTIONS ── */}
        {customSections.map(section => (
          <SectionCard key={section.id} section={section} {...sectionProps} />
        ))}

        {/* New section form / button */}
        {creating ? (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input
              autoFocus
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateSection(); if (e.key === 'Escape') { setCreating(false); setNewTitle(''); } }}
              placeholder="Section name e.g. Technical Skills, Certifications…"
              className="form-input"
              style={{ flex: 1, fontSize: 13 }}
            />
            <button onClick={handleCreateSection} disabled={!newTitle.trim()} className="btn btn-success btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={13} /> Create
            </button>
            <button onClick={() => { setCreating(false); setNewTitle(''); }} className="btn btn-ghost btn-sm">
              Cancel
            </button>
          </div>
        ) : (
          <AddBtn onClick={() => setCreating(true)} size={13} title="New Skill Section" />
        )}

      </div>
    </div>
  );
};
